/**
 * terminalBus — BroadcastChannel pub/sub singleton
 * Syncs symbol, timeframe, and crosshair across tabs/windows.
 */

export type BusMessage =
  | { type: 'SYMBOL_CHANGE';    symbol: string }
  | { type: 'PERIOD_CHANGE';    period: string }
  | { type: 'INTERVAL_CHANGE';  interval: string }
  | { type: 'CROSSHAIR_MOVE';   time: number | string; price: number }

type BusListener = (msg: BusMessage) => void

class TerminalBus {
  private channel: BroadcastChannel | null = null
  private listeners = new Set<BusListener>()
  private rafId: number | null = null
  private pendingCrosshair: BusMessage | null = null

  constructor() {
    if (typeof window === 'undefined' || !('BroadcastChannel' in window)) return
    this.channel = new BroadcastChannel('bloomberg-terminal')
    this.channel.onmessage = (e: MessageEvent<BusMessage>) => {
      this.listeners.forEach((fn) => fn(e.data))
    }
    window.addEventListener('beforeunload', () => this.close())
  }

  subscribe(fn: BusListener): () => void {
    this.listeners.add(fn)
    return () => this.listeners.delete(fn)
  }

  publish(msg: BusMessage): void {
    if (!this.channel) return
    // Throttle crosshair to one rAF frame to avoid flooding
    if (msg.type === 'CROSSHAIR_MOVE') {
      this.pendingCrosshair = msg
      if (this.rafId === null) {
        this.rafId = requestAnimationFrame(() => {
          if (this.pendingCrosshair) this.channel?.postMessage(this.pendingCrosshair)
          this.pendingCrosshair = null
          this.rafId = null
        })
      }
      return
    }
    this.channel.postMessage(msg)
  }

  close(): void {
    if (this.rafId !== null) { cancelAnimationFrame(this.rafId); this.rafId = null }
    this.channel?.close()
    this.channel = null
  }
}

export const terminalBus = new TerminalBus()
